// src/components/TeamItemsPage/TeamItemModal.js

import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styles from './TeamItemModal.module.css';
import axios from 'axios';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  FaTimes, FaCheckCircle, FaClock, FaFlag, FaCalendarAlt, FaUser, FaEdit, FaTag, 
  FaExclamationTriangle, FaClipboardList, FaPen, FaFileAlt, FaFutbol, FaCheck,
  FaLayerGroup
} from 'react-icons/fa';
import { useUser } from '../../context/UserContext';
import { canManageProgram } from '../../hooks/useProgramsApi';
import { formatRole as formatRoleUtil } from '../../utils/roleFormatting';

const TeamItemModal = ({
  isOpen,
  onClose,
  programId,
  itemToEdit,
  onItemAdded,
  onItemUpdated,
}) => {
  const { userClubs } = useUser();
  const [title, setTitle] = useState('');
  const [eventId, setEventId] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState(null); // Using Date object
  const [status, setStatus] = useState('To Do');
  const [priority, setPriority] = useState('Medium');
  const [tags, setTags] = useState('');

  const [events, setEvents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [clubId, setClubId] = useState(null);

  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingPlayers, setLoadingPlayers] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Program permissions state
  const [programPermissions, setProgramPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  // Authorization logic - similar to EventsPage
  const canManage = useMemo(() => {
    // Check if user has club management role
    const hasClubRole = Array.isArray(userClubs) && userClubs.some((m) => {
      const membershipClubId = m.club?._id || m.club;
      const role = (m.role || '').toLowerCase();
      const isTargetClub = String(membershipClubId) === String(clubId);
      const isOwnerOrManager = ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
      return isTargetClub && isOwnerOrManager;
    });

    // Check if user has program management permissions
    const hasProgramPermissions = canManageProgram(programPermissions);

    return hasClubRole || hasProgramPermissions;
  }, [userClubs, clubId, programPermissions]);

  // Fetch program permissions for current user
  useEffect(() => {
    if (!programId) return;
    
    const fetchProgramPermissions = async () => {
      try {
        setPermissionsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/permissions`);
        if (response.ok) {
          const data = await response.json();
          setProgramPermissions(data?.permissions || []);
        } else {
          setProgramPermissions([]);
        }
      } catch (error) {
        setProgramPermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchProgramPermissions();
  }, [programId]);

  // Fetch program details to get clubId
  useEffect(() => {
    const fetchProgramDetails = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}`
        );
        const program = response.data;
        
        // Extract clubId correctly (handle both string and object cases)
        const extractedClubId =
          program.clubId && typeof program.clubId === 'object'
            ? program.clubId._id
            : program.clubId;
            
        setClubId(extractedClubId);
      } catch (err) {
        // Silent fail - will use program-level permissions only
      }
    };

    if (programId) {
      fetchProgramDetails();
    }
  }, [programId]);

  // Fetch events
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/events/${programId}`
        );
        setEvents(response.data);
        setLoadingEvents(false);
      } catch (err) {
        setLoadingEvents(false);
      }
    };

    if (programId) {
      fetchEvents();
    }
  }, [programId]);

  // Fetch players (SAME AS AWARD EDIT MODAL)
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        setLoadingPlayers(true);

        // Fetch teams first
        let teamsData = [];
        try {
          const teamsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams/program/${programId}`);
          if (teamsResponse.ok) {
            const teams = await teamsResponse.json();
            teamsData = Array.isArray(teams) ? teams : [teams];
          }
        } catch (teamError) {
          // Silent fail - will fetch players without team data
        }

        // Fetch available players
        try {
          const playersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/teams/program/${programId}/available-players`);
          if (playersResponse.ok) {
            const playersData = await playersResponse.json();
            
            if (playersData.availablePlayers && Array.isArray(playersData.availablePlayers)) {
              // Create team lookup map - use userId to match with availablePlayers
              const teamMap = {};
              teamsData.forEach(team => {
                if (team.players && Array.isArray(team.players)) {
                  team.players.forEach(player => {
                    // Use userId field to match with available players
                    const playerId = player.userId || player._id;
                    teamMap[playerId] = {
                      teamId: team._id,
                      teamName: team.name,
                      roleKey: player.roleKey || 'player'
                    };
                  });
                }
              });

              const transformedPlayers = playersData.availablePlayers.map(player => {
                const teamInfo = teamMap[player._id] || {};
                
                return {
                  _id: player._id,
                  userId: player._id,
                  firstName: player.firstName || 'Unknown',
                  lastName: player.lastName || 'Player',
                  fullName: player.fullName || `${player.firstName || ''} ${player.lastName || ''}`.trim(),
                  teamId: teamInfo.teamId || player.teamId,
                  teamName: teamInfo.teamName || player.teamName || 'No Team',
                  roleKey: teamInfo.roleKey || player.roleKey || 'player',
                  roleName: player.roleName
                };
              });
              
              setPlayers(transformedPlayers);
            }
          }
        } catch (playerError) {
          setPlayers([]);
        }
      } catch (error) {
        setPlayers([]);
      } finally {
        setLoadingPlayers(false);
      }
    };

    if (programId) {
      fetchPlayers();
    }
  }, [programId]);

  // Format role names using data-driven utility (roles loaded from database)
  const formatRole = formatRoleUtil;

  // Helper function to format player display (SAME AS AWARD EDIT MODAL)
  const formatUserDisplay = (player) => {
    const fullName = player.fullName || `${player.firstName || ''} ${player.lastName || ''}`.trim();
    const teamName = player.teamName || 'No Team';
    const role = formatRole(player.roleKey);
    
    return `${fullName} | ${teamName} • ${role}`;
  };

  // Initialize form fields when editing or using template
  useEffect(() => {
    if (itemToEdit) {
      setTitle(itemToEdit.title || '');
      setEventId(itemToEdit.event?._id || '');
      setAssignedTo(itemToEdit.assignedTo?._id || '');
      setDescription(itemToEdit.description || '');
      // Only set dueDate if not using template (templates shouldn't have pre-set dates)
      setDueDate(itemToEdit.isTemplate ? null : (itemToEdit.dueDate ? new Date(itemToEdit.dueDate) : null));
      setStatus(itemToEdit.status || 'To Do');
      setPriority(itemToEdit.priority || 'Medium');
      setTags(itemToEdit.tags ? (Array.isArray(itemToEdit.tags) ? itemToEdit.tags.join(', ') : itemToEdit.tags) : '');
    } else {
      // Reset form
      setTitle('');
      setEventId('');
      setAssignedTo('');
      setDescription('');
      setDueDate(null);
      setStatus('To Do');
      setPriority('Medium');
      setTags('');
    }
  }, [itemToEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user has management permissions
    if (!canManage) {
      alert('You need management permissions to create or edit team items.');
      return;
    }
    
    setSubmitting(true);
    setError('');

    // Form validation
    if (!title.trim()) {
      setError('Title is required');
      setSubmitting(false);
      return;
    }

    if (!status) {
      setError('Status is required');
      setSubmitting(false);
      return;
    }

    if (!priority) {
      setError('Priority is required');
      setSubmitting(false);
      return;
    }

    try {
      // Clean up the data to match backend expectations
      const itemData = {
        title: title.trim(),
        description: description.trim() || '',
        status,
        priority,
        tags: tags ? tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        programId,
      };

      // Only add optional fields if they have values
      if (eventId) {
        itemData.event = eventId;
      }
      if (assignedTo) {
        itemData.assignedTo = assignedTo;
      }
      if (dueDate) {
        itemData.dueDate = dueDate.toISOString();
      }

      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      };

      let response;
      // Check for actual _id to determine if this is an edit or create
      // Templates have isTemplate flag but no _id, so they should be treated as new items
      if (itemToEdit && itemToEdit._id && !itemToEdit.isTemplate) {
        // Update existing assignment
        response = await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/team-items/${itemToEdit._id}`,
          itemData,
          config
        );
        onItemUpdated(response.data);
      } else {
        // Create new assignment (including from templates)
        response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/team-items`,
          itemData,
          config
        );
        onItemAdded(response.data);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        alert('You don\'t have permission to perform this action.');
        return;
      }
      
      let errorMessage = 'Failed to save assignment';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Show loading state while permissions are being fetched
  if (permissionsLoading) {
    return createPortal(
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          <div className={styles.modalBody}>
            <div>Loading permissions...</div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle} id="modal-title">
            <FaClipboardList className={styles.modalTitleIcon} />
            {itemToEdit && itemToEdit._id && !itemToEdit.isTemplate ? 'Edit Assignment' : 'Create Assignment'}
          </div>
          <div className={styles.headerControls}>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              title="Close"
              aria-label="Close modal"
            >
              <FaTimes />
            </button>
          </div>
        </div>
        
        <div className={styles.modalBody}>
          <form className={styles.form} onSubmit={handleSubmit}>
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  <FaPen className={styles.formLabelIcon} />
                  Title
                </label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter assignment title"
                  required
                  aria-required="true"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  <FaFlag className={styles.formLabelIcon} />
                  Priority
                </label>
                <select
                  className={styles.formSelect}
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  required
                  aria-required="true"
                >
                  <option value="Low">🟢 Low</option>
                  <option value="Medium">🟡 Medium</option>
                  <option value="High">🔴 High</option>
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  <FaFutbol className={styles.formLabelIcon} />
                  Event (Optional)
                </label>
                {loadingEvents ? (
                  <p>Loading events...</p>
                ) : (
                  <select
                    className={styles.formSelect}
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                  >
                    <option value="">-- Select Event --</option>
                    {events.map((event) => {
                      const eventDate = new Date(event.dateTime?.start || event.date);
                      const formattedDate = eventDate.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      });
                      const eventType = event.type || 'Event';
                      
                      // Format teams information
                      let teamsInfo = '';
                      if (event.teams) {
                        if (event.type === 'Practice') {
                          teamsInfo = ` | ${event.teams.home.name}`;
                        } else {
                          teamsInfo = ` | ${event.teams.home.name} vs ${event.teams.away.name}`;
                        }
                      } else if (event.opponentTeam) {
                        teamsInfo = ` | Home vs ${event.opponentTeam}`;
                      }
                      
                      const displayText = `${event.title} - ${formattedDate} - ${eventType}${teamsInfo}`;
                      
                      return (
                      <option key={event._id} value={event._id}>
                          {displayText}
                      </option>
                      );
                    })}
                  </select>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  <FaUser className={styles.formLabelIcon} />
                  Assign To (Optional)
                </label>
                {loadingPlayers ? (
                  <p>Loading players...</p>
                ) : (
                  <select
                    className={styles.formSelect}
                    value={assignedTo}
                    onChange={(e) => setAssignedTo(e.target.value)}
                  >
                    <option value="">-- Select Player --</option>
                    {players.map((player) => (
                      <option key={player._id} value={player._id}>
                        {formatUserDisplay(player)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                <FaFileAlt className={styles.formLabelIcon} />
                Description (Optional)
              </label>
              <textarea
                className={styles.formTextarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter a detailed description..."
                rows={4}
              ></textarea>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  <FaCalendarAlt className={styles.formLabelIcon} />
                  Due Date (Optional)
                </label>
                <div className={styles.datePickerWrapper}>
                  <DatePicker
                    selected={dueDate}
                    onChange={(date) => setDueDate(date)}
                    dateFormat="MMM d, yyyy"
                    placeholderText="Select due date"
                    isClearable
                    minDate={new Date()}
                    className={styles.datePickerInput}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  <FaLayerGroup className={styles.formLabelIcon} />
                  Status
                </label>
                <select
                  className={styles.formSelect}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  required
                  aria-required="true"
                >
                  <option value="To Do">📋 To Do</option>
                  <option value="In Progress">▶️ In Progress</option>
                  <option value="Done">✅ Done</option>
                </select>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                <FaTag className={styles.formLabelIcon} />
                Tags (Optional)
              </label>
              <input
                type="text"
                className={styles.formInput}
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="equipment, training, game-prep (comma separated)"
              />
              <span className={styles.formHint}>Add tags to organize and filter assignments</span>
            </div>
          </form>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onClose}
            disabled={submitting}
          >
            <FaTimes /> Cancel
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>⏳ Saving...</>
            ) : (
              <>
                <FaCheck /> {(itemToEdit && itemToEdit._id && !itemToEdit.isTemplate) ? 'Save Changes' : 'Create Assignment'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

TeamItemModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  programId: PropTypes.string.isRequired,
  itemToEdit: PropTypes.object,
  onItemAdded: PropTypes.func.isRequired,
  onItemUpdated: PropTypes.func.isRequired,
};

export default TeamItemModal;
